import React from 'react';
import { ArrowRight, Brain, Code, Users, Clock, FileCheck, MessageSquare } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const ServiceCustom = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen text-white pt-20 md:pt-32">
      {/* Hero Section - Responsive */}
      <div className="container mx-auto px-4 mb-8 md:mb-16">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-block px-4 py-2 rounded-full bg-[#017D7E]/10 text-[#017D7E] mb-6 md:mb-8 text-sm md:text-base">
            Formation Sur Mesure
          </div>
          <h1 className="text-3xl md:text-5xl font-bold mb-4 md:mb-6">
            Des Formations Adaptées à
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-[#017D7E] to-[#00A99D]">
              Vos Besoins Spécifiques
            </span>
          </h1>
          <p className="text-base md:text-xl text-white/70 mb-8 md:mb-12">
            Nous créons des parcours de formation personnalisés pour répondre précisément 
            aux objectifs et aux contraintes de votre organisation
          </p>
          
          <button
            onClick={() => navigate('/contact')}
            className="w-full md:w-auto px-6 py-3 md:px-8 md:py-4 rounded-lg bg-gradient-to-r from-green-500 to-[#017D7E] hover:from-green-600 hover:to-emerald-600 text-white font-medium transition-all flex items-center gap-2 justify-center mx-auto"
          >
            Demander un devis
            <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Process Section - Responsive */}
      <div className="py-12 md:py-24 bg-white/5">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-8 md:mb-16">
              <h2 className="text-2xl md:text-4xl font-bold mb-4 md:mb-6">Notre Processus</h2>
              <p className="text-base md:text-xl text-white/70">
                Une approche méthodique pour créer votre formation sur mesure
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-8">
              <ProcessCard
                number="01"
                icon={MessageSquare}
                title="Analyse des Besoins"
                description="Nous étudions vos objectifs et contraintes spécifiques"
              />
              
              <ProcessCard
                number="02"
                icon={Brain}
                title="Conception"
                description="Création d'un programme adapté à vos attentes"
              />
              
              <ProcessCard
                number="03"
                icon={Code}
                title="Développement"
                description="Production des contenus et supports pédagogiques"
              />
              
              <ProcessCard
                number="04"
                icon={FileCheck}
                title="Validation"
                description="Tests et ajustements avant le déploiement"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Features Section - Responsive */}
      <div className="py-12 md:py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-8 md:mb-16">
              <h2 className="text-2xl md:text-4xl font-bold mb-4 md:mb-6">
                Pourquoi Choisir une
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#017D7E] to-[#00A99D]">
                  {" "}Formation Sur Mesure
                </span>
              </h2>
              <p className="text-base md:text-xl text-white/70">
                Des solutions adaptées à vos enjeux spécifiques
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
              <FeatureCard
                icon={Users}
                title="Personnalisation Complète"
                description="Contenus, rythme et modalités adaptés à vos besoins"
                benefits={[
                  "Objectifs spécifiques",
                  "Cas pratiques de votre secteur",
                  "Adaptation au niveau des apprenants"
                ]}
              />
              
              <FeatureCard
                icon={Clock}
                title="Flexibilité Maximale"
                description="Organisation optimale selon vos contraintes"
                benefits={[
                  "Planning sur mesure",
                  "Format présentiel ou distanciel",
                  "Rythme adapté à vos équipes"
                ]}
              />
              
              <FeatureCard
                icon={Brain}
                title="Expertise Dédiée"
                description="Accompagnement personnalisé par nos experts"
                benefits={[
                  "Formateurs spécialisés",
                  "Support technique dédié",
                  "Suivi des progrès"
                ]}
              />
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section - Responsive */}
      <div className="py-12 md:py-24 bg-gradient-to-b from-transparent to-black/30">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-2xl md:text-4xl font-bold mb-4 md:mb-6">
              Prêt à Créer Votre Formation ?
            </h2>
            <p className="text-base md:text-xl text-white/70 mb-8 md:mb-12">
              Discutons de votre projet et créons ensemble le parcours 
              de formation idéal pour vos équipes.
            </p>
            
            <div className="flex flex-col md:flex-row gap-4 md:gap-6 justify-center">
              <button
                onClick={() => navigate('/contact')}
                className="w-full md:w-auto px-6 py-3 md:px-8 md:py-4 rounded-lg bg-gradient-to-r from-green-500 to-[#017D7E] hover:from-green-600 hover:to-emerald-600 text-white font-medium transition-all flex items-center gap-2 justify-center"
              >
                Nous contacter
                <ArrowRight className="w-5 h-5" />
              </button>
              
              <button
                onClick={() => navigate('/catalog')}
                className="w-full md:w-auto px-6 py-3 md:px-8 md:py-4 rounded-lg border border-green-500/50 hover:bg-[#017D7E]/10 text-white font-medium transition-all"
              >
                Voir nos formations
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const ProcessCard = ({ number, icon: Icon, title, description }) => (
  <div className="text-center">
    <div className="relative">
      <div className="w-12 h-12 md:w-16 md:h-16 rounded-xl bg-[#017D7E]/10 flex items-center justify-center mx-auto mb-4 md:mb-6">
        <Icon className="w-6 h-6 md:w-8 md:h-8 text-[#017D7E]" />
      </div>
      <div className="absolute -top-2 -right-2 md:-top-4 md:-right-4 w-6 h-6 md:w-8 md:h-8 rounded-full bg-[#017D7E] flex items-center justify-center text-xs md:text-sm font-bold">
        {number}
      </div>
    </div>
    <h3 className="text-lg md:text-xl font-bold mb-2 md:mb-4">{title}</h3>
    <p className="text-sm md:text-base text-white/70">{description}</p>
  </div>
);

const FeatureCard = ({ icon: Icon, title, description, benefits }) => (
  <div className="bg-white/5 rounded-xl p-6 md:p-8 border border-white/10">
    <div className="w-12 h-12 rounded-lg bg-[#017D7E]/10 flex items-center justify-center mb-4 md:mb-6">
      <Icon className="w-6 h-6 text-[#017D7E]" />
    </div>
    <h3 className="text-lg md:text-xl font-bold mb-3 md:mb-4">{title}</h3>
    <p className="text-sm md:text-base text-white/70 mb-4 md:mb-6">{description}</p>
    <ul className="space-y-2 md:space-y-3">
      {benefits.map((benefit, index) => (
        <li key={index} className="flex items-center gap-2 text-sm md:text-base text-white/70">
          <div className="w-1.5 h-1.5 rounded-full bg-green-400 flex-shrink-0" />
          {benefit}
        </li>
      ))}
    </ul>
  </div>
);

export default ServiceCustom;